<template>
  <div>
    <!-- KPI row + breakdowns. Per-media when one is selected
         (/analytics/medias/:id), cross-media aggregate otherwise
         (/analytics/medias). Card layout matches the Lives tab —
         same skeleton on load, same fade-in stagger, same icon-led
         header. -->
    <div class="kpi-grid mb-6">
      <template v-if="panelLoading.kpis && !data?.sessions">
        <div v-for="i in 6" :key="'skel-stat-' + i" class="bg-white rounded-xl border border-gray-200/60 p-5">
          <LoadingBlock height="80" label="Loading stats..." />
        </div>
      </template>
      <template v-else-if="data?.sessions">
        <div
          v-for="(stat, i) in statCards"
          :key="stat.label"
          :class="[
            'animate-fade-in relative',
            `stagger-${i + 1}`,
            activeInfoCard === (stat.key || stat.label) ? 'z-50' : ''
          ]"
        >
          <div class="bg-white rounded-xl border border-gray-200/60 p-5">
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-1.5">
                <span class="text-sm font-medium text-gray-400 uppercase tracking-wider">{{ stat.label }}</span>
                <div v-if="stat.info" class="relative">
                  <button
                    @click.stop="toggleInfo(stat.key || stat.label)"
                    type="button"
                    class="w-4 h-4 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center text-[10px] font-semibold leading-none transition-colors"
                    :title="stat.info"
                  >?</button>
                  <div
                    v-if="activeInfoCard === (stat.key || stat.label)"
                    @click.stop
                    :class="[
                      'absolute top-full mt-2 z-50 w-64 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm text-gray-600 leading-relaxed',
                      i === statCards.length - 1 ? 'right-0' : 'left-0'
                    ]"
                  >{{ stat.info }}</div>
                </div>
              </div>
              <span :class="['flex items-center justify-center w-9 h-9 rounded-lg', stat.iconBg]" v-html="stat.icon" />
            </div>

            <div class="flex items-baseline gap-2">
              <div class="font-data text-[26px] font-medium text-gray-900 leading-none">{{ stat.value }}</div>
              <DeltaBadge v-if="stat.current != null" :current="stat.current" :previous="stat.previous" :inverse="!!stat.inverse" :formatter="stat.format || formatNumber" />
            </div>
            <p class="text-sm text-gray-400 mt-2">{{ stat.sub }}</p>
          </div>
        </div>
      </template>
    </div>

    <!-- Sessions — single-series line chart. Same chart as the
         Lives tab so cross-media engagement reads identically. -->
    <div class="bg-white rounded-xl border border-gray-200/60 mb-6">
      <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 class="text-[15px] font-semibold text-gray-900">Sessions</h2>
          <p class="text-sm text-gray-400 mt-0.5">Sessions per time bucket</p>
        </div>
        <span v-if="hasData('viewers.concurrent')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
          {{ formatNumber(chartTotal(data.viewers.concurrent, 'count')) }} sessions
        </span>
      </div>
      <div class="p-6">
        <LoadingBlock v-if="panelLoading.sessionsTimeline" height="350" label="Loading chart..." />
        <template v-else-if="hasData('viewers.concurrent')">
          <div class="h-[350px] relative animate-fade-in"><canvas ref="concurrentViewersChart"></canvas></div>
        </template>
        <template v-else><EmptyChart label="No session data for this period" /></template>
      </div>
    </div>

    <!-- Unique Users Chart -->
    <div class="bg-white rounded-xl border border-gray-200/60 mb-6">
      <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 class="text-[15px] font-semibold text-gray-900">Unique Users</h2>
          <p class="text-sm text-gray-400 mt-0.5">Distinct listeners per time bucket</p>
        </div>
        <span v-if="data && data.uniqueUsers" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
          {{ formatNumber(data.uniqueUsers.total) }} total
        </span>
      </div>
      <div class="p-6">
        <LoadingBlock v-if="panelLoading.uniqueUsers" height="350" label="Loading chart..." />
        <template v-else-if="hasData('uniqueUsers.timeline')">
          <div class="h-[350px] relative animate-fade-in"><canvas ref="uniqueUsersChart"></canvas></div>
        </template>
        <template v-else><EmptyChart label="No unique users data for this period" /></template>
      </div>
    </div>

    <!-- Top Content + Geographic Distribution — same component pair
         the Overview / Lives views use. TopContentCard auto-hides
         empty tabs, so on a per-media view only the Categories tab
         (and Medias if cross-media) shows up. -->
    <div class="mb-6">
      <TopContentCard
        :loading="panelLoading.topContent"
        :data="data?.topContent || {}"
        :tabs="['medias', 'categories']"
      />
    </div>
    <div class="mb-6">
      <GeoDistributionCard
        :loading="panelLoading.geoDistribution || panelLoading.geographyMap"
        :data="data?.topGeo || {}"
        :geography="data?.geography || []"
      />
    </div>

    <!-- Demographics — Age / Gender / Language. Same canvases as the
         Lives tab so the two pages read as one design. -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Age Range</h2>
            <p class="text-sm text-gray-400 mt-0.5">Listener age distribution</p>
          </div>
          <span v-if="hasData('demographics.ageRange')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
            {{ formatNumber(chartTotal(data.demographics.ageRange)) }} total
          </span>
        </div>
        <div class="p-6">
          <LoadingBlock v-if="panelLoading.demographics" height="260" label="Loading chart..." />
          <template v-else-if="hasData('demographics.ageRange')">
            <div class="h-[260px] relative animate-fade-in"><canvas ref="ageRangeChart"></canvas></div>
          </template>
          <template v-else><EmptyChart label="No age data available" /></template>
        </div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Gender</h2>
            <p class="text-sm text-gray-400 mt-0.5">Listener gender breakdown</p>
          </div>
          <span v-if="hasData('demographics.gender')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
            {{ formatNumber(chartTotal(data.demographics.gender)) }} total
          </span>
        </div>
        <div class="p-6">
          <LoadingBlock v-if="panelLoading.demographics" height="260" label="Loading chart..." />
          <template v-else-if="hasData('demographics.gender')">
            <div class="h-[260px] relative animate-fade-in"><canvas ref="genderChart"></canvas></div>
          </template>
          <template v-else><EmptyChart label="No gender data available" /></template>
        </div>
      </div>

      <div class="bg-white rounded-xl border border-gray-200/60">
        <div class="px-6 py-3 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 class="text-[15px] font-semibold text-gray-900">Language</h2>
            <p class="text-sm text-gray-400 mt-0.5">Preferred listener languages</p>
          </div>
          <span v-if="hasData('demographics.language')" class="text-sm font-data font-medium text-[var(--color-primary)] bg-[var(--color-primary-light)] px-3 py-1 rounded">
            {{ formatNumber(chartTotal(data.demographics.language)) }} total
          </span>
        </div>
        <div class="p-6">
          <LoadingBlock v-if="panelLoading.demographics" height="260" label="Loading chart..." />
          <template v-else-if="hasData('demographics.language')">
            <div class="h-[260px] relative animate-fade-in"><canvas ref="languageChart"></canvas></div>
          </template>
          <template v-else><EmptyChart label="No language data available" /></template>
        </div>
      </div>
    </div>

    <!-- Operating Systems + Devices + Browsers — same doughnut set as
         the Lives tab. Kept as DonutCard usage (the existing extracted
         component) since the visualization is identical to the Lives
         tab's inline canvases. -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <DonutCard title="Operating Systems" subtitle="OS distribution"          :loading="panelLoading.devices" chart-key="os"       :data="data?.operatingSystems || []" empty-label="No OS data" />
      <DonutCard title="Devices"           subtitle="Device type distribution" :loading="panelLoading.devices" chart-key="devices"  :data="data?.devices          || []" empty-label="No device data" />
      <DonutCard title="Browsers"          subtitle="Browser distribution"     :loading="panelLoading.devices" chart-key="browsers" :data="data?.browsers         || []" empty-label="No browser data" />
    </div>

    <!-- Top Referrers + Top Embed Hosts — same BreakdownCard pattern
         the Overview / Lives views use. -->
    <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      <BreakdownCard title="Top Referrers"   subtitle="Where listeners came from" :loading="panelLoading.referrers" :rows="topRows('referrers')"  empty-label="No referrer data" />
      <BreakdownCard title="Top Embed Hosts" subtitle="Where the player runs"     :loading="panelLoading.referrers" :rows="topRows('embedHosts')" empty-label="No embed host data" />
    </div>

    <!-- Event Breakdown / Quality Metrics / Errors removed 2026-07-09;
         recovery notes at ./removed-charts.md (same three sections
         removed from lives.vue on 2026-07-06). Server-side panel
         endpoints and builders are still in place — re-adding is a
         template + panelTable/panelLoading/computed restore. -->
  </div>
</template>

<script>
import Chart from 'chart.js/auto'
import { mapGetters } from 'vuex'
import api from '../../services/api.js'
import LoadingBlock from '../../components/LoadingBlock.vue'
import DeltaBadge from './components/DeltaBadge.vue'
import TopContentCard from './components/TopContentCard.vue'
import GeoDistributionCard from './components/GeoDistributionCard.vue'
import { notifyError } from '../../services/notifications.js'
import { buildTabReport } from './report-export.js'
import BreakdownCard from './components/BreakdownCard.vue'
import DonutCard from './components/DonutCard.vue'

// Consistent color palette for all charts — kept locally rather than
// shared with the Lives tab so each view stays self-contained.
const COLORS = {
  primary: '#3393ff',
  primaryFill: 'rgba(51, 147, 255, 0.08)',
  bar: 'rgba(51, 147, 255, 0.65)',
  doughnut: ['#3393ff', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280'],
  grid: '#f5f5f5',
  tick: '#a3a3a3'
}

const CHART_SCALES = {
  x: { grid: { display: false }, ticks: { font: { family: 'Poppins', size: 11 }, color: COLORS.tick } },
  y: { grid: { color: COLORS.grid }, ticks: { font: { family: 'JetBrains Mono', size: 11 }, color: COLORS.tick } }
}

// Empty state component
const EmptyChart = {
  props: { label: String },
  template: `
    <div class="flex flex-col items-center justify-center py-12 text-center">
      <div class="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-3">
        <svg class="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
      </div>
      <p class="text-base text-gray-400">{{ label }}</p>
    </div>
  `
}

/**
 * Per-media analytics. Mirrors the Lives tab pattern:
 *   - Read filters from the analytics store (toolbar in the parent
 *     dispatches APPLY on Search).
 *   - Hit /analytics/medias/:id once a media is selected; the
 *     type-specific route looks the id up against the medias
 *     collection so it can't accidentally resolve a live id.
 *   - Hit /analytics/medias (no id) when no media is selected for
 *     the cross-media aggregate. Same payload shape as the
 *     per-media path, just unscoped.
 * The chart set matches the Lives tab so the two views read as one
 * design language. Chart renderers are inlined (rather than extracted
 * to shared components) to stay byte-for-byte with the Lives view.
 */
export default {
  name: 'AnalyticsMedias',
  components: { EmptyChart, LoadingBlock, DeltaBadge, BreakdownCard, DonutCard, TopContentCard, GeoDistributionCard },

  data() {
    return {
      loading: false,
      data: null,
      activeInfoCard: null,
      charts: {},
      // Per-panel loading flags. Same shape as lives.vue so future
      // template surgery (LoadingBlock per section instead of the
      // top-level `loading` boolean) can be added without changing
      // the fetch machinery. Keys match panelTable() below.
      panelLoading: {
        kpis: false, sessionsTimeline: false, uniqueUsers: false, topContent: false,
        geoDistribution: false, geographyMap: false, demographics: false,
        devices: false, referrers: false
      }
    }
  },

  computed: {
    ...mapGetters('analytics', ['applied']),

    /**
     * Six KPI cards driven by the new session model. Mirrors lives.vue
     * — the underlying data is the same shape for both surfaces.
     * See new-sessions.txt for the metric definitions.
     */
    statCards() {
      if (!this.data) return []
      const s = this.data.sessions || {}
      const u = this.data.uniqueUsers || {}
      return [
        {
          key: 'uniqueUsers',
          label: 'Unique Users',
          value: this.formatNumber(u.total),
          current: u.total || 0,
          previous: u.totalPrev,
          sub: 'Distinct listeners in period',
          info: 'Distinct listeners in the period. One person opening the player three times counts as 1.',
          icon: '<svg class="w-4 h-4 text-rose-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></svg>',
          iconBg: 'bg-rose-50'
        },
        {
          key: 'sessions',
          label: 'Sessions',
          value: this.formatNumber(s.total || 0),
          current: s.total || 0,
          previous: s.totalPrev,
          sub: 'Sessions in period',
          info: 'Count of sessions in the window. A session opens on the first play intent and closes on SDK destroy or a 30-minute continuous-pause timeout. A fresh play after an idle-timeout mints a new session.',
          icon: '<svg class="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="18" x2="12" y2="21"/></svg>',
          iconBg: 'bg-blue-50'
        },
        {
          key: 'totalSessionDuration',
          label: 'Total Session Duration',
          value: this.formatDuration(s.totalDurationMs),
          current: s.totalDurationMs || 0,
          previous: s.totalDurationMsPrev,
          format: this.formatDuration,
          sub: 'Wall-clock across sessions',
          info: 'Sum of session_duration across every session. Wall-clock = playing (content + ads) + paused. Includes idle-timeout sessions.',
          icon: '<svg class="w-4 h-4 text-indigo-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
          iconBg: 'bg-indigo-50'
        },
        {
          key: 'avgSessionDuration',
          label: 'Avg Session Duration',
          value: this.formatDuration(s.avgDurationMs),
          current: s.avgDurationMs || 0,
          previous: s.avgDurationMsPrev,
          format: this.formatDuration,
          sub: 'Total duration ÷ sessions',
          info: 'Average wall-clock time a session lasted. Formula: total_session_duration ÷ sessions.',
          icon: '<svg class="w-4 h-4 text-green-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>',
          iconBg: 'bg-green-50'
        },
        {
          key: 'totalWatchTime',
          label: 'Total Watch Time',
          value: this.formatDuration(s.totalWatchTimeMs),
          current: s.totalWatchTimeMs || 0,
          previous: s.totalWatchTimeMsPrev,
          format: this.formatDuration,
          sub: 'Content-only playing time',
          info: 'Sum of time the media element was playing content across every session. Excludes ads (tracked separately in the Ads section) and paused / buffering periods. This is "how long people listened to your content."',
          icon: '<svg class="w-4 h-4 text-violet-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>',
          iconBg: 'bg-violet-50'
        },
        {
          key: 'avgWatchTime',
          label: 'Avg Watch Time',
          value: this.formatDuration(s.avgWatchTimeMs),
          current: s.avgWatchTimeMs || 0,
          previous: s.avgWatchTimeMsPrev,
          format: this.formatDuration,
          sub: 'Content play per session',
          info: 'Average content-play time per session. Formula: total_watch_time ÷ sessions.',
          icon: '<svg class="w-4 h-4 text-amber-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>',
          iconBg: 'bg-amber-50'
        }
      ]
    }
  },

  watch: {
    // Reload whenever the toolbar's APPLY commits new filter values.
    // Deep watch because nested fields mutate in place on Search.
    applied: { handler() { this.loadAnalytics() }, deep: true }
  },

  mounted() {
    // Deep-link support: /analytics/medias?id=X seeds the store so the
    // picked media carries through into the toolbar + this view's first
    // fetch without needing the user to click Search.
    if (this.$route.query.id && !this.applied.mediaId) {
      this.$store.dispatch('analytics/seed', { mediaId: String(this.$route.query.id) })
    }
    // Non-reactive instance state — same convention as lives.vue.
    //   _runId:      monotonic counter — bumped every loadAnalytics() call.
    //                In-flight fetches capture the value when queued;
    //                if it doesn't match by the time the response lands,
    //                the fetch drops silently (a newer filter set took over).
    //   _panelChain: promise chain that serializes every panel fetch so
    //                they hit Mongo one at a time, not all at once.
    this._runId = 0
    this._panelChain = Promise.resolve()
    // AbortController for every in-flight /analytics/* request. Aborted
    // on unmount and re-created per loadAnalytics run so a filter change
    // or route-change cancels the previous run's HTTP work instead of
    // letting it drain and be silently dropped by the runId guard.
    this._abort = new AbortController()

    this.$nextTick(() => this.loadAnalytics())
    // Close info popover on outside click. Bound to document so any
    // click anywhere outside a card's `?`/popover dismisses it (the
    // popover itself stops propagation; the `?` toggles).
    this._onDocClick = () => { this.activeInfoCard = null }
    document.addEventListener('click', this._onDocClick)
  },

  beforeUnmount() {
    // Abort but keep the controller so queued `_fetchPanel` calls
    // inherit an already-aborted signal instead of `undefined`.
    if (this._abort) this._abort.abort()
    Object.values(this.charts).forEach(c => { if (c) c.destroy() })
    if (this._onDocClick) document.removeEventListener('click', this._onDocClick)
  },

  methods: {
    /**
     * Report for the Download button (PDF / CSV) — built from the data
     * this tab already loaded, so it matches the screen. Null while
     * nothing has loaded yet.
     */
    buildReport() {
      if (!this.data) return null
      const a = this.applied
      return buildTabReport({
        title: 'Medias Audience Report',
        kpiCards: this.statCards,
        data: this.data,
        applied: a,
        granularity: this.granularityFor(
          a.dateFrom ? new Date(a.dateFrom).toISOString() : null,
          a.dateTo ? new Date(a.dateTo).toISOString() : null,
          a.presetLabel
        )
      })
    },

    /**
     * Toggle the per-card info popover. Same key click-twice closes;
     * different key swaps to the new card; null closes all.
     */
    toggleInfo(key) {
      this.activeInfoCard = this.activeInfoCard === key ? null : key
    },

    hasData(path) {
      const parts = path.split('.')
      let obj = this.data
      for (const p of parts) {
        obj = obj?.[p]
        if (obj == null) return false
      }
      return Array.isArray(obj) ? obj.length > 0 : !!obj
    },

    /**
     * Load analytics for the currently-applied filters. Per-media when
     * one is selected; cross-media aggregate otherwise. Mirrors the
     * Lives tab's per-live vs unscoped split — and its per-panel
     * serialized queue: each panel is one /analytics/{panel}/{scope}
     * request that appends onto `_panelChain`, so DB pressure stays
     * capped at one query at a time. Results deep-merge into
     * `this.data` as they land.
     */
    async loadAnalytics() {
      const a = this.applied
      const params = {}
      if (a.dateFrom) params.dateFrom = new Date(a.dateFrom).toISOString()
      if (a.dateTo)   params.dateTo   = new Date(a.dateTo).toISOString()
      if (a.appName)  params.appName  = a.appName
      if (a.categoryIds?.length) params.categoryId = a.categoryIds.join(',')
      params.granularity = this.granularityFor(params.dateFrom, params.dateTo, a.presetLabel)
      // Per-media (medias/:id) vs cross-media (medias) scope. Type-
      // specific routes look the id up against the matching collection
      // so a hex collision with a live can never resolve into the wrong view.
      const scope = a.mediaId ? `medias/${a.mediaId}` : 'medias'

      // ---- TEMP: isolation testing ----
      // Restrict the eager fetches to a specific subset while we verify
      // per-panel behaviour after the writer rewrite. Set to null to
      // load every panel.
      const ISOLATE_PANELS = null

      this.loading = true
      this.$emit('loading', true)
      this.data = null
      Object.values(this.charts).forEach(c => { if (c) c.destroy() })
      this.charts = {}

      const panels = this.panelTable(scope, params)
        .filter(p => !ISOLATE_PANELS || ISOLATE_PANELS.includes(p.key))

      // Only flip on the loading flags for panels we're actually going
      // to fetch — the rest stay false so any future v-if="panelLoading.*"
      // in the templates doesn't spin on nothing.
      const activeKeys = new Set(panels.map(p => p.key))
      for (const key of Object.keys(this.panelLoading)) {
        this.panelLoading[key] = activeKeys.has(key)
      }

      // Abort any in-flight requests from the previous run so the
      // network work is actually cancelled — not just its result
      // discarded downstream via the runId guard.
      if (this._abort) this._abort.abort()
      this._abort = new AbortController()

      // Bump the run id so any in-flight fetches from a superseded
      // loadAnalytics() (previous filter set) drop silently. Reset the
      // chain so new enqueues start a fresh promise chain.
      const runId = ++this._runId
      this._panelChain = Promise.resolve()

      let tail = Promise.resolve()
      for (const p of panels) tail = this._enqueue(() => this._fetchPanel(p, runId))

      try {
        await tail
      } finally {
        if (this._runId === runId) {
          this.loading = false
          this.$emit('loading', false)
          // Individual chart renders now fire from each panel's `render`
          // callback when its data lands — no end-of-queue call needed.
        }
      }
    },

    /**
     * Table of every panel the view knows how to fetch. Callable so
     * render closures can capture the correct `this`. Kept in the
     * order the queue should hit them.
     */
    panelTable(scope, params) {
      // `geographyMap` is queued last on purpose — it runs a sampled
      // session scan (~3s) that other panels shouldn't wait behind.
      // The map pane inside GeoDistributionCard flips on as soon as
      // `data.geography` populates.
      return [
        { key: 'kpis',             url: 'kpis',              scope, params },
        { key: 'sessionsTimeline', url: 'sessions-timeline', scope, params, render: () => this.renderConcurrentViewersChart() },
        { key: 'uniqueUsers',      url: 'unique-users',      scope, params, render: () => this.renderUniqueUsersChart() },
        { key: 'topContent',       url: 'top-content',       scope, params },
        { key: 'geoDistribution',  url: 'geo-distribution',  scope, params },
        { key: 'demographics',     url: 'demographics',      scope, params, render: () => {
          this.renderAgeRangeChart()
          this.renderGenderChart()
          this.renderLanguageChart()
        } },
        { key: 'devices',          url: 'devices',           scope, params },
        { key: 'referrers',        url: 'referrers',         scope, params },
        { key: 'geographyMap',     url: 'geography-map',     scope, params }
      ]
    },

    /**
     * Append a task onto the shared `_panelChain` so panel fetches run
     * one at a time in enqueue order. Errors are swallowed here so a
     * single failing panel doesn't derail the chain — `_fetchPanel`
     * surfaces the error via notifyError before rejecting.
     */
    _enqueue(task) {
      this._panelChain = (this._panelChain || Promise.resolve())
        .then(() => task().catch(() => {}))
      return this._panelChain
    },

    /**
     * Run one panel's fetch, drop the result if a newer run has
     * superseded us, and deep-merge the response into `this.data` so
     * per-namespace fields from different panels accumulate instead
     * of overwriting each other (two panels can both write to
     * `viewers`, etc.).
     */
    async _fetchPanel(p, runId) {
      if (runId != null && this._runId !== runId) return
      const signal = this._abort ? this._abort.signal : undefined
      // Short-circuit if aborted — queued panels behind an in-flight
      // request shouldn't reach the network after unmount / re-run.
      if (signal && signal.aborted) return
      try {
        const res = await api.get(`/analytics/${p.url}/${p.scope}`, { params: p.params, silent: true, signal })
        if (runId != null && this._runId !== runId) return
        const incoming = res.data || {}
        const merged = { ...(this.data || {}) }
        for (const [k, v] of Object.entries(incoming)) {
          merged[k] = (v && typeof v === 'object' && !Array.isArray(v) && merged[k] && typeof merged[k] === 'object' && !Array.isArray(merged[k]))
            ? { ...merged[k], ...v }
            : v
        }
        this.data = merged
      } catch (err) {
        if (runId != null && this._runId !== runId) return
        // AbortController.abort() — expected during nav / filter change.
        if (err?.name === 'CanceledError' || err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') return
        notifyError(err?.message || `Failed to load ${p.url}`)
      } finally {
        if (runId == null || this._runId === runId) {
          this.panelLoading[p.key] = false
          // Invoke the panel's render callback AFTER the loading flag
          // flips false — the template's v-if="panelLoading.X" branch
          // hides the <canvas> while loading, so the ref is null until
          // Vue re-evaluates. $nextTick waits for that re-render, then
          // the canvas exists and Chart.js can attach. Missing this
          // block is why Sessions / Unique Users / Age Range / Gender
          // / Language charts silently no-op'd on the Medias tab even
          // though their KPI badges showed values from the same data.
          if (p.render) {
            await this.$nextTick()
            p.render()
          }
        }
      }
    },

    renderCharts() {
      this.renderUniqueUsersChart()
      this.renderConcurrentViewersChart()
      this.renderAgeRangeChart()
      this.renderGenderChart()
      this.renderLanguageChart()
    },

    destroyChart(key) {
      if (this.charts[key]) { this.charts[key].destroy(); this.charts[key] = null }
    },

    /**
     * Resolve the bucket granularity from the API response. New API
     * surfaces `data.viewers.granularity` ('minute'|'hour'|'day'|'month'|'year');
     * older payloads only have `isHourly`. Default to 'hour' if neither
     * is set so charts still render against unknown payloads.
     */
    bucketGranularity() {
      const g = this.data?.viewers?.granularity
      if (g) return g
      return this.data?.viewers?.isHourly === false ? 'day' : 'hour'
    },

    /**
     * Decide the chart bucket size for a given date range. Presets map
     * to fixed buckets; Custom falls through to a duration-based mapping.
     * Sent to the API as `?granularity=...` so the server picks the
     * matching rollup tier; the same value drives x-axis label
     * formatting locally.
     */
    granularityFor(from, to, presetLabel) {
      const presetMap = {
        'Last Hour':     'minute',
        'Last 6 Hours':  'hour',
        'Today':         'hour',
        'Last 24 Hours': 'hour',
        'Yesterday':     'hour',
        'Last 7 Days':   'day',
        'Last 30 Days':  'day',
        'This Month':    'day',
        'Last Month':    'day',
        'Last Year':     'month'
      }
      if (presetMap[presetLabel]) return presetMap[presetLabel]
      if (!from || !to) return 'hour'
      const ms = new Date(to) - new Date(from)
      const hours = ms / 3600000
      const days  = hours / 24
      if (hours < 1)   return 'minute'
      if (hours < 24)  return 'hour'
      if (days  < 31)  return 'day'
      if (days  < 365) return 'month'
      return 'year'
    },

    /**
     * Render a bucket key as a human label given the active granularity.
     *   minute → "14:23"
     *   hour   → "2 PM"
     *   day    → "May 1"
     *   month  → "May 2026"
     *   year   → "2026"
     */
    formatChartLabel(raw, granularity = 'hour', localDate = null) {
      if (!raw) return raw
      if (granularity === 'minute') {
        if (localDate) {
          const hh = String(localDate.getHours()).padStart(2, '0')
          const mm = String(localDate.getMinutes()).padStart(2, '0')
          return `${hh}:${mm}`
        }
        const m = raw.match(/(\d{2}):(\d{2})$/)
        return m ? `${m[1]}:${m[2]}` : raw
      }
      if (granularity === 'hour') {
        if (localDate) {
          const h = localDate.getHours()
          const ampm = h >= 12 ? 'PM' : 'AM'
          const h12 = h % 12 || 12
          return `${h12} ${ampm}`
        }
        const match = raw.match(/(\d{2}):00$/)
        if (match) {
          const h = parseInt(match[1])
          const ampm = h >= 12 ? 'PM' : 'AM'
          const h12 = h % 12 || 12
          return `${h12} ${ampm}`
        }
        return raw
      }
      if (granularity === 'day') {
        const d = new Date(raw + 'T12:00:00')
        if (isNaN(d)) return raw
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      }
      if (granularity === 'month') {
        const d = new Date(raw + '-01T12:00:00')
        if (isNaN(d)) return raw
        return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
      }
      return raw
    },

    /**
     * Walk the applied date range in bucket-sized steps and emit a
     * (label, value) pair for every bucket — filling missing API rows
     * with zero so the timeline doesn't collapse around gaps. Same
     * logic as the Lives tab.
     */
    /**
     * Roll day-grain rows (`{_id: 'YYYY-MM-DD', count: N}`) up to a
     * coarser bucket ('month' / 'year'). Returns rows unchanged for
     * 'day' or any finer granularity — callers force those to 'day'
     * before calling since finer-than-day rollup isn't meaningful for
     * daily-materialized data.
     */
    dailyToGranularity(rows, target) {
      if (target !== 'month' && target !== 'year') return rows
      const bucket = {}
      for (const r of rows) {
        const iso = r._id || ''
        const key = target === 'month' ? iso.substring(0, 7) : iso.substring(0, 4)
        if (!key) continue
        bucket[key] = (bucket[key] || 0) + (r.count || 0)
      }
      return Object.entries(bucket)
        .map(([k, v]) => ({ _id: k, count: v }))
        .sort((a, b) => a._id.localeCompare(b._id))
    },

    fillTimeGaps(data, valueKey, granularity) {
      const map = {}
      data.forEach(item => { map[item._id] = item[valueKey] || 0 })

      const startIso = this.applied?.dateFrom ? new Date(this.applied.dateFrom).toISOString() : (data.length && data[0]._id)
      const endIso   = this.applied?.dateTo   ? new Date(this.applied.dateTo).toISOString()   : (data.length && data[data.length - 1]._id)
      if (!startIso || !endIso) return { labels: [], values: [] }

      const start = new Date(this.parseFillStart(startIso, granularity))
      const end   = new Date(this.parseFillStart(endIso, granularity))
      const labels = []
      const values = []

      const step = (d) => {
        if (granularity === 'minute') d.setUTCMinutes(d.getUTCMinutes() + 1)
        if (granularity === 'hour')   d.setUTCHours(d.getUTCHours() + 1)
        if (granularity === 'day')    d.setUTCDate(d.getUTCDate() + 1)
        if (granularity === 'month')  d.setUTCMonth(d.getUTCMonth() + 1)
        if (granularity === 'year')   d.setUTCFullYear(d.getUTCFullYear() + 1)
      }

      const keyFor = (d) => {
        const iso = d.toISOString()
        if (granularity === 'minute') return iso.substring(0, 16).replace('T', ' ')
        if (granularity === 'hour')   return iso.substring(0, 13).replace('T', ' ') + ':00'
        if (granularity === 'day')    return iso.substring(0, 10)
        if (granularity === 'month')  return iso.substring(0, 7)
        return String(d.getUTCFullYear())
      }

      for (let d = new Date(start); d <= end; step(d)) {
        const key = keyFor(d)
        const usesLocal = granularity === 'minute' || granularity === 'hour'
        labels.push(this.formatChartLabel(key, granularity, usesLocal ? d : null))
        values.push(map[key] || 0)
      }
      return { labels, values }
    },

    parseFillStart(s, granularity) {
      if (granularity === 'year')  return Date.UTC(parseInt(s.substring(0, 4)), 0, 1)
      if (granularity === 'month') return Date.UTC(parseInt(s.substring(0, 4)), parseInt(s.substring(5, 7)) - 1 || 0, 1)
      if (granularity === 'day')   return new Date(s.substring(0, 10) + 'T00:00:00Z').getTime()
      const d = new Date(s)
      if (granularity === 'hour')   d.setUTCMinutes(0, 0, 0)
      if (granularity === 'minute') d.setUTCSeconds(0, 0)
      return d.getTime()
    },

    renderUniqueUsersChart() {
      const ctx = this.$refs.uniqueUsersChart
      if (!ctx) return
      this.destroyChart('uniqueUsers')
      // Server returns rows at the requested granularity for
      // minute/hour/day (hour/minute via a session scan, day via the
      // materialized collection with a session-scan fallback). For
      // month/year it still returns day-grain rows and we roll them up
      // client-side — dailyToGranularity is a no-op for finer targets.
      const granularity = this.bucketGranularity()
      const d = this.dailyToGranularity(this.data.uniqueUsers?.timeline || [], granularity)
      const { labels, values } = this.fillTimeGaps(d, 'count', granularity)
      // Violet tint to visually distinguish from the blue sessions / streams
      // chart.
      const violet = '#8b5cf6'
      const violetFill = 'rgba(139, 92, 246, 0.08)'
      this.charts.uniqueUsers = new Chart(ctx, {
        type: 'line',
        data: {
          labels,
          datasets: [{ data: values, borderColor: violet, backgroundColor: violetFill, tension: 0.4, fill: true, borderWidth: 2, pointRadius: 2, pointHoverRadius: 5 }]
        },
        options: { responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false } }, scales: { ...CHART_SCALES } }
      })
    },

    /**
     * Sessions chart — single filled line, sessions per bucket. The
     * previous "Streams (5s+)" second line was retired since the new
     * session model already scopes sessions to actual plays.
     */
    renderConcurrentViewersChart() {
      const ctx = this.$refs.concurrentViewersChart
      if (!ctx) return
      this.destroyChart('concurrent')
      const granularity = this.bucketGranularity()
      const sessionsData = this.data.viewers?.concurrent || []
      const sessions = this.fillTimeGaps(sessionsData, 'count', granularity)
      this.charts.concurrent = new Chart(ctx, {
        type: 'line',
        data: {
          labels: sessions.labels,
          datasets: [
            {
              label: 'Sessions',
              data: sessions.values,
              borderColor: COLORS.primary,
              backgroundColor: COLORS.primaryFill,
              tension: 0.4,
              fill: true,
              borderWidth: 2,
              pointRadius: 2,
              pointHoverRadius: 5
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          interaction: { mode: 'index', intersect: false },
          plugins: {
            // Single-series chart — legend adds no information; the
            // card header already says "Sessions".
            legend: { display: false }
          },
          scales: { ...CHART_SCALES }
        }
      })
    },

    renderAgeRangeChart () {
      const ctx = this.$refs.ageRangeChart
      if (!ctx) return
      this.destroyChart('ageRange')
      const order = ['18+', '21+', '18-34', '18-49', '21-34', '18-24', '21-24', '25-34', '25-49', '25-54', '35-44', '45-54', '55-64', '55+', '65+']
      const vals = order.map(age => {
        const f = (this.data.demographics?.ageRange || []).find(d => d._id === age)
        return f ? f.count : 0
      })
      this.charts.ageRange = new Chart(ctx, {
        type: 'bar',
        data: { labels: order, datasets: [{ data: vals, backgroundColor: COLORS.bar, borderRadius: 4 }] },
        options: {
          responsive: true,
          maintainAspectRatio: false, animation: false,
          plugins: { legend: { display: false } },
          scales: {
            ...CHART_SCALES,
            x: { ...CHART_SCALES.x, ticks: { ...CHART_SCALES.x.ticks, font: { family: 'Poppins', size: 10 } } }
          }
        }
      })
    },

    renderGenderChart() {
      const ctx = this.$refs.genderChart
      if (!ctx) return
      this.destroyChart('gender')
      const d = this.data.demographics?.gender || []
      this.charts.gender = new Chart(ctx, {
        type: 'doughnut',
        data: {
          labels: d.map(x => x._id),
          datasets: [{ data: d.map(x => x.count), backgroundColor: COLORS.doughnut.slice(0, d.length) }]
        },
        options: {
          responsive: true, maintainAspectRatio: false, animation: false, cutout: '65%',
          plugins: { legend: { position: 'bottom', labels: { font: { family: 'Poppins', size: 12 }, padding: 16, usePointStyle: true, pointStyle: 'circle' } } }
        }
      })
    },

    renderLanguageChart() {
      const ctx = this.$refs.languageChart
      if (!ctx) return
      this.destroyChart('language')
      // Map keyed in lowercase ISO codes; values come in uppercase
      // post-demographics-normalization, so lowercase the key on
      // lookup to handle both cases without duplicating entries.
      const langNames = { en: 'English', es: 'Spanish', fr: 'French', de: 'German', pt: 'Portuguese', it: 'Italian', ja: 'Japanese', zh: 'Chinese', ko: 'Korean', ar: 'Arabic', hi: 'Hindi', ru: 'Russian' }
      const d = this.data.demographics?.language || []
      this.charts.language = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: d.map(x => langNames[String(x._id || '').toLowerCase()] || x._id),
          datasets: [{ data: d.map(x => x.count), backgroundColor: COLORS.bar, borderRadius: 4 }]
        },
        options: {
          indexAxis: 'y', responsive: true, maintainAspectRatio: false, animation: false, plugins: { legend: { display: false } },
          scales: {
            x: { grid: { color: COLORS.grid }, ticks: { font: { family: 'JetBrains Mono', size: 10 }, color: COLORS.tick } },
            y: { grid: { display: false }, ticks: { font: { family: 'Poppins', size: 11 }, color: '#737373' } }
          }
        }
      })
    },

    topRows(path) {
      const parts = path.split('.')
      let arr = this.data
      for (const p of parts) arr = arr?.[p]
      if (!Array.isArray(arr)) return []
      return arr.slice(0, 10).map(r => ({
        id: r._id,
        label: r.title || r._id || 'Unknown',
        count: r.count || 0
      }))
    },

    /**
     * Sum a numeric field across an array. Used for the "X total" badges
     * in each chart header. Defaults to summing `count`; pass another key
     * for arrays whose value field differs.
     */
    chartTotal(arr, key = 'count') {
      if (!Array.isArray(arr)) return 0
      return arr.reduce((s, x) => s + (Number(x?.[key]) || 0), 0)
    },

    formatNumber(n) {
      if (n == null || isNaN(n)) return '—'
      if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M'
      if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K'
      return Number(n).toLocaleString()
    },
    formatDuration(ms) {
      if (!ms) return '0s'
      const s = Math.floor(ms / 1000), m = Math.floor(s / 60), h = Math.floor(m / 60)
      if (h > 0) return `${h}h ${m % 60}m`
      if (m > 0) return `${m}m ${s % 60}s`
      return `${s}s`
    }
  }
}
</script>
